import type { TastingNote, Visit } from '@recipes/contracts';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@recipes/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/feedback/toaster';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { formatDay } from './format';

const NEW_VISIT = '__new__';

/** The user's other visits as targets for moving a dish or merging. */
function useOtherVisits(currentVisitId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.tasting.visits({ scope: 'mine', limit: 50 }),
    queryFn: () => api.visits.list({ scope: 'mine', limit: 50 }),
    enabled,
    select: (page) => page.items.filter((v) => v.id !== currentVisitId),
  });
}

function VisitOptionLabel({ visit }: { visit: Visit }) {
  const { t } = useTranslation();
  const dishes = visit.notes.map((n) => n.dishName).filter(Boolean);
  return (
    <>
      {visit.place?.name ?? t('tasting.visit.unknownPlace')} · {formatDay(visit.visitedAt)}
      {dishes.length > 0 && ` · ${dishes.slice(0, 3).join(', ')}`}
    </>
  );
}

/**
 * Moves one dish into another own visit, or splits it off into a new visit (which keeps time
 * and position of this one and gets its restaurant confirmed afterwards).
 */
export function MoveDishDialog({
  visit,
  note,
  onClose,
  onMoved,
}: {
  visit: Visit;
  note: TastingNote;
  onClose: () => void;
  /** Called with the visit the dish now belongs to. */
  onMoved: (target: Visit) => void;
}) {
  const { t } = useTranslation();
  const selectId = useId();
  const others = useOtherVisits(visit.id, true);
  const canSplit = visit.notes.length > 1;
  const [target, setTarget] = useState('');

  const move = useMutation({
    mutationFn: () =>
      api.tastingNotes.move(
        note.id,
        target === NEW_VISIT ? { newVisit: true } : { targetVisitId: target },
      ),
    onSuccess: (moved) => {
      toast.success(t('tasting.tools.moved'));
      onMoved(moved);
    },
    onError: (e) => toastError(e),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('tasting.tools.moveTitle', {
              dish: note.dishName ?? t('tasting.visit.unnamedDish'),
            })}
          </DialogTitle>
          <DialogDescription>{t('tasting.tools.moveHint')}</DialogDescription>
        </DialogHeader>
        {others.isLoading ? (
          <Spinner label={t('app.loading')} />
        ) : (
          <div>
            <Label htmlFor={selectId}>{t('tasting.tools.moveTarget')}</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger id={selectId} className="mt-1" data-testid="move-target">
                <SelectValue placeholder={t('tasting.tools.choose')} />
              </SelectTrigger>
              <SelectContent>
                {canSplit && (
                  <SelectItem value={NEW_VISIT}>{t('tasting.tools.newVisit')}</SelectItem>
                )}
                {(others.data ?? []).map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    <VisitOptionLabel visit={v} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!canSplit && !(others.data ?? []).length && (
              <Alert variant="info" className="mt-3">
                {t('tasting.tools.noTargets')}
              </Alert>
            )}
            {visit.notes.length === 1 && target && target !== NEW_VISIT && (
              <p className="mt-2 text-xs text-muted-foreground">
                {t('tasting.tools.lastDishHint')}
              </p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!target}
            loading={move.isPending}
            onClick={() => move.mutate()}
            data-testid="move-confirm"
          >
            {t('tasting.tools.moveAction')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Pulls all dishes of another own visit into this one; the other visit is deleted. */
export function MergeVisitDialog({
  visit,
  onClose,
  onMerged,
}: {
  visit: Visit;
  onClose: () => void;
  onMerged: (merged: Visit) => void;
}) {
  const { t } = useTranslation();
  const selectId = useId();
  const others = useOtherVisits(visit.id, true);
  const [source, setSource] = useState('');
  const chosen = (others.data ?? []).find((v) => v.id === source);

  const merge = useMutation({
    mutationFn: () => api.visits.merge(visit.id, source),
    onSuccess: (merged) => {
      toast.success(t('tasting.tools.merged'));
      onMerged(merged);
    },
    onError: (e) => toastError(e),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('tasting.tools.mergeTitle')}</DialogTitle>
          <DialogDescription>{t('tasting.tools.mergeHint')}</DialogDescription>
        </DialogHeader>
        {others.isLoading ? (
          <Spinner label={t('app.loading')} />
        ) : (others.data ?? []).length === 0 ? (
          <Alert variant="info">{t('tasting.tools.noTargets')}</Alert>
        ) : (
          <div>
            <Label htmlFor={selectId}>{t('tasting.tools.mergeSource')}</Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger id={selectId} className="mt-1" data-testid="merge-source">
                <SelectValue placeholder={t('tasting.tools.choose')} />
              </SelectTrigger>
              <SelectContent>
                {(others.data ?? []).map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    <VisitOptionLabel visit={v} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {chosen && (
              <p className="mt-2 text-sm">
                {t('tasting.tools.mergeConsequence', {
                  count: chosen.notes.length,
                  place: chosen.place?.name ?? t('tasting.visit.unknownPlace'),
                })}
              </p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!source}
            loading={merge.isPending}
            onClick={() => merge.mutate()}
            data-testid="merge-confirm"
          >
            {t('tasting.tools.mergeAction')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
